import { Form, Input, Select, Button, Space } from "antd";
import { useEffect } from "react";
import {
  ICreatePermissionGroup,
  IEditPermissionGroup,
  PermissionGroup,
} from "@shared/lib/types/permissions";
import { ToastService } from "@shared/lib/services";
import { useGetPermissionGroups } from "../lib/hooks";
import { useGetAdminPermissions } from "@entities/admin/lib/hooks/useGetAdminPermissions";

export interface PermissionGroupFormProps {
  onSubmit: (values: ICreatePermissionGroup | IEditPermissionGroup) => void;
  isSubmitting: boolean;
  onCancel: () => void;
  initialValues?: PermissionGroup | null;
}

export function PermissionGroupForm({
  onSubmit,
  isSubmitting,
  onCancel,
  initialValues,
}: PermissionGroupFormProps) {
  const [form] = Form.useForm();

  const { adminPermissions, isFetching } = useGetAdminPermissions();
  const allPermissions = [
    ...(adminPermissions?.admin || []),
    ...(adminPermissions?.brand || []),
  ];

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        name: initialValues.name,
        description: initialValues.description || "",
        permissions: initialValues.permissions || [],
      });
    } else {
      form.resetFields();
    }
  }, [initialValues, form]);

  const handleSubmit = (values: ICreatePermissionGroup) => {
    if (initialValues) {
      onSubmit({
        ...values,
        _id: initialValues._id,
      } as IEditPermissionGroup);
    } else {
      onSubmit(values);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      onFinishFailed={({ errorFields }) => {
        if (errorFields.length) {
          ToastService.error(
            "Error Submitting Form. Please fill all required fields."
          );
        }
      }}
      initialValues={{
        name: initialValues?.name || "",
        description: initialValues?.description || "",
        permissions: initialValues?.permissions || [],
      }}
    >
      <Form.Item
        name="name"
        label="Name"
        rules={[{ required: true, message: "Name is required" }]}
      >
        <Input placeholder="Enter permission group name" />
      </Form.Item>

      <Form.Item name="description" label="Description">
        <Input.TextArea placeholder="Enter description (optional)" rows={3} />
      </Form.Item>

      <Form.Item
        name="permissions"
        label="Permissions"
        rules={[
          { required: true, message: "At least one permission is required" },
        ]}
      >
        <Select
          loading={isFetching}
          mode="multiple"
          placeholder="Select or add permissions"
          style={{ width: "100%" }}
          options={allPermissions.map((permission) => ({
            label: permission,
            value: permission,
          }))}
          tokenSeparators={[","]}
        />
      </Form.Item>

      <Form.Item>
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button
            onClick={onCancel}
            disabled={isSubmitting}
            style={{ minWidth: 80 }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
            style={{ minWidth: 80 }}
          >
            Save
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}
